import { useState } from 'react';
import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { CheckPointType } from '@/api/entities/check-point';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { getLanguage, getTranslatedItem } from '@/utils/language.utils';
import { Avatar } from '@/components/Avatar';
import { useDuplicateCheckPoint } from '@/hooks/check-point.hooks';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { showModal } from '@/utils/modal.utils';

export const CheckPointTableData = <TSchema extends FieldValues>({
  t,
  data,
  setCurrentPage,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();
  const { mutate: duplicateCheckPoint } = useDuplicateCheckPoint({
    t,
    handleSuccess: () => {
      setCurrentPage?.(1);
      window.scrollTo({ top: 0, behavior: 'auto' });
    },
  });
  const [selectedCheckPointId, setSelectedCheckPointId] = useState<string>();

  const onDuplicateCheckPoint = (id: string) => {
    const { languageCode } = getLanguage();

    const idWithLanguageCode = {
      id,
      languageCode,
    };

    duplicateCheckPoint(idWithLanguageCode);
  };

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={4 + 2}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/check-point/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        const translatedCheckPoint = getTranslatedItem(
          entry?.translatedCheckPoints
        );

        const isVideo = entry?.type === CheckPointType.VIDEO;

        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            <TableCell
              tdClassName="sticky-select-column"
              content={
                <Checkbox
                  checked={Boolean(
                    selectedTableData?.find((item) => item?.id === entry?.id)
                  )}
                  onChange={() => onSelectedItem(entry?.id)}
                />
              }
            />
            <TableCell content={translatedCheckPoint?.name || entry?.name} />
            <TableCell
              content={
                <div className="flex flex-col justify-center mr-1">
                  <div className="flex gap-2 text-slate-500 text-2sm font-semibold uppercase">
                    <i
                      className={twMerge(
                        'h-0 ki-outline text-[17px] text-black',
                        isVideo ? 'ki-youtube' : 'ki-graph-3'
                      )}
                    />
                    {isVideo ? t('common:video') : t('common:photo')}
                  </div>
                </div>
              }
            />
            <TableCell
              content={
                <div className="flex gap-2 max-[1400px]:flex-wrap">
                  <div className="flex items-center gap-3 border border-dashed border-slate-400 bg-purple-F7F5FF/65 px-2.5 py-1.5 rounded-lg max-[1400px]:flex-1 max-sm:min-w-fit">
                    <div className="flex flex-1 items-center text-[13.5px] text-slate-500 font-medium leading-[17px] max-md:line-clamp-3">
                      {t('reportIcon')}
                    </div>
                    <Avatar
                      imageUrl={entry?.reportIcon}
                      containerClassName="w-[60px] h-[60px] border-0 shadow-0"
                    />
                  </div>
                  <div className="flex items-center gap-3 border border-dashed border-slate-400 bg-purple-F7F5FF/65 px-2.5 py-1.5 rounded-lg max-[1400px]:flex-1 max-sm:min-w-fit">
                    <div className="flex flex-1 items-center text-[13.5px] text-slate-500 font-medium leading-[17px] max-md:line-clamp-3">
                      {t('appIcon')}
                    </div>
                    <Avatar
                      imageUrl={entry?.appIcon}
                      containerClassName="w-[60px] h-[60px] border-0 shadow-0"
                    />
                  </div>
                  <div className="flex items-center gap-3 border border-dashed border-slate-400 bg-purple-F7F5FF/65 px-2.5 py-1.5 rounded-lg max-[1400px]:min-w-[270px] max-[1400px]:flex-1 max-md:min-w-fit">
                    <div className="flex flex-1 items-center text-[13.5px] text-slate-500 font-medium leading-[17px] max-md:line-clamp-3">
                      {t('appMask')}
                    </div>
                    <Avatar
                      imageUrl={entry?.appSketch}
                      containerClassName="w-[60px] h-[60px] border-0 shadow-0"
                    />
                  </div>
                </div>
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/check-point/edit/${entry?.id}`}
                  />
                  <SubMenuItem
                    leftIconClassName="ki-abstract-21 !text-[18.5px]"
                    text={t('common:duplicate')}
                    onClick={() => {
                      setSelectedCheckPointId(entry?.id);
                      showModal('#confirmation-duplicate-checkpoint-modal');
                    }}
                  />
                  <SubMenuItem
                    text={t('common:deactivate')}
                    disabled={!entry?.active || pendingDeactivate}
                    leftIconClassName="ki-trash !text-[19px]"
                    textClassName="line-clamp-1"
                    onClick={() => onDeactivate([entry])}
                  />
                </TableMenu>
              }
            />
          </tr>
        );
      })}
      <tr>
        <TableCell
          content={
            <ConfirmationModal
              id="confirmation-duplicate-checkpoint-modal"
              title={t('common:confirmYourAction')}
              description={`${t('areYouSureToDuplicateThisCheckPoint')} ?`}
              onConfirmLabel={t('common:duplicate')}
              onConfirm={() => {
                onDuplicateCheckPoint(selectedCheckPointId);
              }}
            />
          }
        />
      </tr>
    </>
  );
};
